import React, { useState, useEffect } from 'react';
import styled from 'styled-components';
import { useTheme } from '../../context/ThemeContext';
import { getTheme } from '../../theme';
import Card from '../common/Card';
import Button from '../common/Button';
import { api } from '../../services/api';
import * as MuiIcons from '@mui/icons-material';
import { Clear } from '@mui/icons-material';

const FunnelContainer = styled(Card)`
  padding: ${props => getTheme(props.theme?.isDark).spacing.lg};
`;

const FunnelTitle = styled.h2`
  font-size: 20px;
  font-weight: 700;
  color: ${props => getTheme(props.theme?.isDark).colors.text};
  margin: 0 0 ${props => getTheme(props.theme?.isDark).spacing.md} 0;
  ${props => props.theme?.isDark && `
    text-shadow: 0 0 10px rgba(25, 184, 136, 0.3);
  `}
`;

const FunnelSteps = styled.div`
  display: grid;
  grid-template-columns: repeat(auto-fit, minmax(200px, 1fr));
  gap: ${props => getTheme(props.theme?.isDark).spacing.md};
  
  @media (max-width: 768px) {
    grid-template-columns: repeat(auto-fit, minmax(150px, 1fr));
  }
`;

const FunnelStep = styled.div`
  display: flex;
  flex-direction: column;
  padding: ${props => getTheme(props.theme?.isDark).spacing.sm};
  border-radius: ${props => getTheme(props.theme?.isDark).borderRadius.md};
  background-color: ${props => getTheme(props.theme?.isDark).colors.surface};
  border: 1px solid ${props => props.isSelected 
    ? (props.color || getTheme(props.theme?.isDark).colors.primary)
    : getTheme(props.theme?.isDark).colors.border};
  transition: all 0.2s ease;
  cursor: pointer;
  
  &:hover {
    transform: translateY(-2px);
    border-color: ${props => props.color || getTheme(props.theme?.isDark).colors.primary};
    ${props => props.theme?.isDark && `
      box-shadow: 0 0 15px rgba(25, 184, 136, 0.2);
    `}
  }
`;

const StepHeader = styled.div`
  display: flex;
  align-items: center;
  gap: ${props => getTheme(props.theme?.isDark).spacing.xs};
  margin-bottom: ${props => getTheme(props.theme?.isDark).spacing.xs};
`;

const StepIcon = styled.div`
  width: 32px;
  height: 32px;
  border-radius: ${props => getTheme(props.theme?.isDark).borderRadius.sm};
  background-color: ${props => props.color || getTheme(props.theme?.isDark).colors.primary}20;
  display: flex;
  align-items: center;
  justify-content: center;
  color: ${props => props.color || getTheme(props.theme?.isDark).colors.primary};
  flex-shrink: 0;
  font-size: 16px;
  ${props => props.theme?.isDark && `
    box-shadow: 0 0 10px ${props.color || getTheme(props.theme?.isDark).colors.primary}40;
  `}
`;

const StepInfo = styled.div`
  flex: 1;
  min-width: 0;
`;

const StepLabel = styled.div`
  font-size: 11px;
  color: ${props => getTheme(props.theme?.isDark).colors.textSecondary};
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
`;

const StepValue = styled.div`
  font-size: 18px;
  font-weight: 700;
  color: ${props => getTheme(props.theme?.isDark).colors.text};
  line-height: 1.2;
  ${props => props.theme?.isDark && `
    text-shadow: 0 0 10px rgba(25, 184, 136, 0.3);
  `}
`;

const StepPercentage = styled.div`
  font-size: 12px;
  font-weight: 600;
  color: ${props => props.color || getTheme(props.theme?.isDark).colors.primary};
  margin-left: auto;
`;

const ProgressBar = styled.div`
  width: 100%;
  height: 4px;
  background-color: ${props => getTheme(props.theme?.isDark).colors.border};
  border-radius: ${props => getTheme(props.theme?.isDark).borderRadius.sm};
  overflow: hidden;
  margin-top: ${props => getTheme(props.theme?.isDark).spacing.xs};
`;

const ProgressFill = styled.div`
  height: 100%;
  background: linear-gradient(90deg, 
    ${props => props.color || getTheme(props.theme?.isDark).colors.primary}, 
    ${props => props.color || getTheme(props.theme?.isDark).colors.primary}dd
  );
  width: ${props => props.percentage}%;
  transition: width 0.3s ease;
  ${props => props.theme?.isDark && `
    box-shadow: 0 0 10px ${props.color || getTheme(props.theme?.isDark).colors.primary}60;
  `}
`;

const LoadingContainer = styled.div`
  display: flex;
  justify-content: center;
  align-items: center;
  padding: ${props => getTheme(props.theme?.isDark).spacing.xxl};
  color: ${props => getTheme(props.theme?.isDark).colors.textSecondary};
`;

const ErrorContainer = styled.div`
  padding: ${props => getTheme(props.theme?.isDark).spacing.lg};
  color: ${props => getTheme(props.theme?.isDark).colors.error || '#ff4444'};
  text-align: center;
`;

const FilterContainer = styled.div`
  display: flex;
  flex-wrap: wrap;
  gap: ${props => getTheme(props.theme?.isDark).spacing.sm};
  margin-bottom: ${props => getTheme(props.theme?.isDark).spacing.md};
  padding: ${props => getTheme(props.theme?.isDark).spacing.sm};
  background-color: ${props => getTheme(props.theme?.isDark).colors.surface};
  border-radius: ${props => getTheme(props.theme?.isDark).borderRadius.md};
  border: 1px solid ${props => getTheme(props.theme?.isDark).colors.border};
`;

const FilterGroup = styled.div`
  display: flex;
  flex-direction: column;
  gap: ${props => getTheme(props.theme?.isDark).spacing.xs};
  min-width: 120px;
  flex: 1;
`;

const FilterLabel = styled.label`
  font-size: 12px;
  color: ${props => getTheme(props.theme?.isDark).colors.textSecondary};
  font-weight: 600;
`;

const FilterInput = styled.input`
  padding: ${props => getTheme(props.theme?.isDark).spacing.sm};
  border: 1px solid ${props => getTheme(props.theme?.isDark).colors.border};
  border-radius: ${props => getTheme(props.theme?.isDark).borderRadius.md};
  background-color: ${props => getTheme(props.theme?.isDark).colors.background};
  color: ${props => getTheme(props.theme?.isDark).colors.text};
  font-size: 14px;
  font-family: ${props => getTheme(props.theme?.isDark).fonts.primary};

  &:focus {
    outline: none;
    border-color: ${props => getTheme(props.theme?.isDark).colors.primary};
    ${props => props.theme?.isDark && `
      box-shadow: 0 0 10px rgba(25, 184, 136, 0.3);
    `}
  }
`;

const FilterSelect = styled.select`
  padding: ${props => getTheme(props.theme?.isDark).spacing.sm};
  border: 1px solid ${props => getTheme(props.theme?.isDark).colors.border};
  border-radius: ${props => getTheme(props.theme?.isDark).borderRadius.md};
  background-color: ${props => getTheme(props.theme?.isDark).colors.background};
  color: ${props => getTheme(props.theme?.isDark).colors.text};
  font-size: 14px;
  font-family: ${props => getTheme(props.theme?.isDark).fonts.primary};
  cursor: pointer;

  &:focus {
    outline: none;
    border-color: ${props => getTheme(props.theme?.isDark).colors.primary};
    ${props => props.theme?.isDark && `
      box-shadow: 0 0 10px rgba(25, 184, 136, 0.3);
    `}
  }
`;

const FilterActions = styled.div`
  display: flex;
  align-items: flex-end;
  gap: ${props => getTheme(props.theme?.isDark).spacing.sm};
`;

const CustomerFunnel = ({ filters: externalFilters, onFiltersChange, onFunnelClick }) => {
  const { isDark } = useTheme();
  const theme = { isDark };
  const [funnelData, setFunnelData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [selectedFunnelId, setSelectedFunnelId] = useState(null);
  const [filters, setFilters] = useState(externalFilters || {
    startDate: '',
    endDate: '',
    status: '',
  });

  useEffect(() => {
    fetchFunnelData();
  }, [filters]); // eslint-disable-line react-hooks/exhaustive-deps

  useEffect(() => {
    if (externalFilters) {
      setFilters(externalFilters);
    }
  }, [externalFilters]);

  const fetchFunnelData = async () => {
    try {
      setLoading(true);
      setError(null);
      const params = {};
      if (filters.startDate) params.startDate = filters.startDate;
      if (filters.endDate) params.endDate = filters.endDate;
      if (filters.status) params.status = filters.status;
      
      const data = await api.getCustomerFunnelStats(params);
      setFunnelData(data);
    } catch (err) {
      console.error('Error fetching funnel data:', err);
      setError('Failed to load funnel data');
    } finally {
      setLoading(false);
    }
  };

  const handleFilterChange = (key, value) => {
    const newFilters = { ...filters, [key]: value };
    setFilters(newFilters);
    if (onFiltersChange) {
      onFiltersChange(newFilters);
    }
  };

  const clearFilters = () => {
    const clearedFilters = { startDate: '', endDate: '', status: '' };
    setFilters(clearedFilters);
    if (onFiltersChange) {
      onFiltersChange(clearedFilters);
    }
  };

  const hasActiveFilters = filters.startDate || filters.endDate || filters.status;


  if (loading) {
    return (
      <FunnelContainer theme={theme}>
        <LoadingContainer theme={theme}>Loading funnel data...</LoadingContainer>
      </FunnelContainer>
    );
  }

  if (error) {
    return (
      <FunnelContainer theme={theme}>
        <ErrorContainer theme={theme}>{error}</ErrorContainer>
      </FunnelContainer>
    );
  }

  if (!funnelData) {
    return null;
  }

  // Get icon component dynamically
  const getIcon = (iconName) => {
    const IconComponent = MuiIcons[iconName] || MuiIcons.People;
    return <IconComponent />;
  };

  const { funnels } = funnelData;
  
  // If funnels array exists, use it (dynamic), otherwise use legacy format
  const steps = funnels 
    ? funnels.map(funnel => ({
        id: funnel.id,
        label: funnel.name,
        value: funnel.value,
        percentage: funnel.percentage,
        icon: getIcon(funnel.icon),
        color: funnel.color,
      }))
    : [
        {
          id: null,
          label: 'Total Customers',
          value: funnelData.totalCustomers || 0,
          percentage: 100,
          icon: getIcon('People'),
          color: '#1976d2',
        },
      ];

  const handleFunnelStepClick = (funnelId) => {
    if (selectedFunnelId === funnelId) {
      // If clicking the same funnel, deselect it
      setSelectedFunnelId(null);
      if (onFunnelClick) {
        onFunnelClick(null);
      }
    } else {
      // Select the new funnel
      setSelectedFunnelId(funnelId);
      if (onFunnelClick) {
        onFunnelClick(funnelId);
      }
    }
  };

  return (
    <FunnelContainer theme={theme}>
      <FunnelTitle theme={theme}>Customer Funnel</FunnelTitle>
      
      <FilterContainer theme={theme}>
        <FilterGroup theme={theme}>
          <FilterLabel theme={theme}>Start Date</FilterLabel>
          <FilterInput
            type="date"
            value={filters.startDate}
            onChange={(e) => handleFilterChange('startDate', e.target.value)}
            theme={theme}
          />
        </FilterGroup>
        <FilterGroup theme={theme}>
          <FilterLabel theme={theme}>End Date</FilterLabel>
          <FilterInput
            type="date"
            value={filters.endDate}
            onChange={(e) => handleFilterChange('endDate', e.target.value)}
            theme={theme}
          />
        </FilterGroup>
        <FilterGroup theme={theme}>
          <FilterLabel theme={theme}>Status</FilterLabel>
          <FilterSelect
            value={filters.status}
            onChange={(e) => handleFilterChange('status', e.target.value)}
            theme={theme}
          >
            <option value="">All Statuses</option>
            <option value="incomplete">Incomplete</option>
            <option value="completed">Completed</option>
            <option value="active">Active</option>
            <option value="banned">Banned</option>
          </FilterSelect>
        </FilterGroup>
        {hasActiveFilters && (
          <FilterActions theme={theme}>
            <Button onClick={clearFilters} theme={theme} variant="outline">
              <Clear /> Clear Filters
            </Button>
          </FilterActions>
        )}
      </FilterContainer>

      <FunnelSteps theme={theme}>
        {steps.map((step, index) => (
          <FunnelStep 
            key={index} 
            theme={theme}
            color={step.color}
            isSelected={selectedFunnelId === step.id}
            onClick={() => step.id && handleFunnelStepClick(step.id)}
          >
            <StepHeader theme={theme}>
              <StepIcon theme={theme} color={step.color}>
                {step.icon}
              </StepIcon>
              <StepInfo>
                <StepLabel theme={theme} title={step.label}>{step.label}</StepLabel>
                <StepValue theme={theme}>{step.value.toLocaleString()}</StepValue>
              </StepInfo>
              <StepPercentage theme={theme} color={step.color}>
                {step.percentage}%
              </StepPercentage>
            </StepHeader>
            <ProgressBar theme={theme}>
              <ProgressFill
                theme={theme}
                color={step.color}
                percentage={step.percentage}
              />
            </ProgressBar>
          </FunnelStep>
        ))}
      </FunnelSteps>
    </FunnelContainer>
  );
};

export default CustomerFunnel;

